import { useState, useCallback, useEffect } from 'react';
import { CommentRecord, CommentWithContentContext } from 'shared/types/contentTypes';
import { getComments } from '../api/api';

interface UseCommentRepliesProps {
  contentId: string;
  commentId: string;
  limit?: number;
}

interface CommentRepliesState {
  replies: CommentWithContentContext[];
  isLoading: boolean;
  error: Error | null;
  hasMoreReplies: boolean;
  lastEvaluatedKey?: string;
}

/**
 * Hook for fetching and managing replies to a specific comment
 */
export const useCommentReplies = ({ contentId, commentId, limit = 10 }: UseCommentRepliesProps) => {
  const [state, setState] = useState<CommentRepliesState>({
    replies: [],
    isLoading: false,
    error: null,
    hasMoreReplies: false,
    lastEvaluatedKey: undefined
  });

  // Function to fetch replies
  const fetchReplies = useCallback(async (refresh = false) => {
    if (!contentId || !commentId) {
      return;
    }

    setState(prev => ({ ...prev, isLoading: true, error: null }));

    try {
      // Use lastEvaluatedKey for pagination, unless refreshing
      const lastKey = refresh ? undefined : state.lastEvaluatedKey;
      
      const response = await getComments({
        contentId,
        parentCommentId: commentId,
        limit,
        lastEvaluatedKey: lastKey
      });

      // Add contentContext to each reply for ContextualComment component
      const repliesWithContext = response.comments.map(reply => ({
        ...reply,
        contentContext: {
          contentId,
          contentType: 'comment',
          contentTitle: ''
        }
      }));

      setState(prev => {
        // If refreshing, replace the replies; otherwise, append them
        const newReplies = refresh 
          ? repliesWithContext
          : [...prev.replies, ...repliesWithContext];

        return {
          replies: newReplies,
          isLoading: false,
          error: null,
          hasMoreReplies: !!response.lastEvaluatedKey,
          lastEvaluatedKey: response.lastEvaluatedKey
        };
      });
    } catch (error) {
      console.error('Error fetching comment replies:', error);
      setState(prev => ({
        ...prev,
        isLoading: false,
        error: error instanceof Error 
          ? error 
          : new Error('Failed to load replies')
      }));
    }
  }, [contentId, commentId, limit, state.lastEvaluatedKey]);

  // Load more replies with pagination
  const loadMoreReplies = useCallback(() => {
    if (state.isLoading || !state.hasMoreReplies) return;
    fetchReplies(false);
  }, [fetchReplies, state.isLoading, state.hasMoreReplies]);

  // Refresh all replies
  const refreshReplies = useCallback(() => {
    fetchReplies(true);
  }, [fetchReplies]);

  // Initial fetch on mount
  useEffect(() => {
    if (contentId && commentId) {
      fetchReplies(true);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [contentId, commentId]); // Intentionally excluding fetchReplies to avoid constant refreshing

  // Add a reply to the local state (for optimistic updates)
  const addReply = useCallback((reply: CommentRecord | CommentWithContentContext) => {
    // Ensure the reply has contentContext
    const replyWithContext = 'contentContext' in reply
      ? reply as CommentWithContentContext
      : {
          ...reply,
          contentContext: {
            contentId,
            contentType: 'comment',
            contentTitle: 'Reply'
          }
        };

    setState(prev => ({
      ...prev,
      replies: [replyWithContext, ...prev.replies]
    }));
  }, [contentId]);

  // Update a reply in the local state
  const updateReply = useCallback((commentId: string, updatedData: Partial<CommentRecord>) => {
    setState(prev => ({
      ...prev,
      replies: prev.replies.map(reply => 
        reply.commentId === commentId 
          ? { ...reply, ...updatedData } 
          : reply
      )
    }));
  }, []);

  // Remove a reply from the local state (or mark as deleted)
  const removeReply = useCallback((commentId: string, softDelete = true) => {
    setState(prev => {
      if (softDelete) {
        // Soft delete: Mark as deleted but keep in the list
        return {
          ...prev,
          replies: prev.replies.map(reply => 
            reply.commentId === commentId 
              ? { ...reply, isDeleted: true, text: '[Comment deleted]' } 
              : reply
          )
        };
      } else {
        // Hard delete: Remove from the list
        return {
          ...prev,
          replies: prev.replies.filter(reply => reply.commentId !== commentId)
        };
      }
    });
  }, []);

  return {
    ...state,
    loadMoreReplies,
    refreshReplies,
    addReply,
    updateReply,
    removeReply
  };
};

export default useCommentReplies;